<template lang="pug">
div
    v-progress-circular(v-if="validationInProgress" color="primary" indeterminate size="50")

    v-card.card-envelope(v-else flat)
        v-card-title {{$t('passwordRecovery.request.title')}}

        template(v-if="!isCodeValid")
            v-alert(type="warning" icon="fas fa-exclamation-triangle" :dense="$vuetify.breakpoint.xs")
                p {{$t('passwordRecovery.request.codeIsInvalidTryAgainMessage')}}
                p.mb-0 {{$t('passwordRecovery.request.contactSupportMessage')}}
            v-card-actions
                v-btn(
                    color="primary" width="100%"
                    :height="buttonHeight"
                    @click="onRetryButtonClick"
                ) {{$t('passwordRecovery.request.retryPasswordRecoveryButtonLabel')}}

        template(v-else-if="passwordChangedSuccessfully")
            v-alert(type="info" icon="fas fa-info-circle" :dense="$vuetify.breakpoint.xs")
                div(v-html="$t('passwordRecovery.request.passwordChangedSuccessfullyMessage')")

            v-card-actions.justify-center
                v-btn(
                    color="primary" width="100%"
                    :height="buttonHeight"
                    @click="onFinalizeButtonClick"
                ) {{$t('passwordRecovery.request.loginButtonLabel')}}

        template(v-else)
            v-form(v-model="isFormValid" ref="form" :lazy-validation="true" :disabled="isSaveInProgress" @submit.prevent="onFormSubmit")
                v-card-text
                    v-row
                        v-text-field(
                            v-model="newPassword" outlined  :autofocus="true"
                            :rules="passwordValidationRules"
                            :label="$t('passwordRecovery.request.newPasswordFieldLabel')"
                        )
                v-card-actions.justify-center
                    v-btn(
                        color="primary" type="submit" width="100%"
                        :disabled="!isFormValid" :loading="isSaveInProgress" :height="buttonHeight"
                    ) {{ $t('passwordRecovery.request.setNewPasswordButtonLabel') }}

</template>

<script lang="ts">
import Vue from 'vue'
import {Component, Prop} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import {AUTH_CHANGE_PASSWORD_WITH_RECOVERY_CODE, AUTH_VALIDATE_RECOVERY_CODE} from '@/store/action-types'
import {ChangePasswordWithRecoveryCodePayload} from '@/types/payload'
import {valueIsRequired, checkPasswordLength} from '@/services/validation'

@Component({})
export default class SetNewPassword extends Vue {
  $refs!: {
    form: Vue & { validate(): boolean, resetValidation(): void }
  }

  @Prop({required: true}) recoveryCode!: string

  private validationInProgress: boolean = false
  private isSaveInProgress: boolean = false
  private isCodeValid: boolean = false
  private isFormValid: boolean = false
  private passwordChangedSuccessfully: boolean = false
  private newPassword: string | null = null
  private passwordValidationRules: Function[] = [valueIsRequired, checkPasswordLength]

  get buttonHeight(): number | undefined {
    if (this.$vuetify.breakpoint.smAndDown) {
      return 64
    }
  }

  @Action(AUTH_VALIDATE_RECOVERY_CODE) validateRecoveryCode!: { (recoveryCode: string): Promise<void> }
  @Action(AUTH_CHANGE_PASSWORD_WITH_RECOVERY_CODE) changePassword!: { (payload: ChangePasswordWithRecoveryCodePayload): Promise<void> }

  onRetryButtonClick(): void {
    this.$router.replace({name: 'PasswordRecovery'})
  }

  onFinalizeButtonClick(): void {
    this.$router.replace({name: 'Login'})
  }

  async onFormSubmit(): Promise<void> {
    if (this.$refs.form.validate()) {
      const payload: ChangePasswordWithRecoveryCodePayload = {
        password: this.newPassword!,
        recoveryCode: this.recoveryCode
      }

      try {
        this.isSaveInProgress = true

        await this.changePassword(payload)
        this.passwordChangedSuccessfully = true
      } catch (e) {
        this.isCodeValid = false
      } finally {
        this.isSaveInProgress = false
      }
    }
  }

  async validateCode(): Promise<void> {
    try {
      this.validationInProgress = true

      await this.validateRecoveryCode(this.recoveryCode)
      this.isCodeValid = true
    } finally {
      this.validationInProgress = false
    }
  }

  mounted() {
    this.validateCode()
  }
}
</script>

<style lang="stylus" scoped>
.card-envelope
  max-width 600px
</style>
